/**
 * Form helpers for the Unit drawer (Safety V1 parity).
 *
 * The save endpoints accept ONLY the keys that changed, so the drawer keeps the
 * loaded record as `original` and diffs the edited copy against it on Save.
 */

import type { UnitChildKey, UnitFieldError } from '../../../types/safetySystem';
import type { ChildRow } from './ticketForm';
import { childRowsForSave } from './ticketForm';

export type FormRecord = Record<string, unknown>;

/** null / undefined and '' are the same "empty" on the wire; everything else compares by string. */
const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/**
 * Keys of `current` whose normalized value differs from `original`.
 * A cleared value is reported as '' (the backend blanks the column).
 */
export function diffChanges(original: FormRecord, current: FormRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(current)) {
    const next = norm(current[key]);
    if (next !== norm(original[key])) out[key] = current[key] === null || current[key] === undefined ? '' : current[key];
  }
  return out;
}

export const isDirty = (original: FormRecord, current: FormRecord): boolean =>
  Object.keys(diffChanges(original, current)).length > 0;

/**
 * Backend date columns arrive as 'YYYY-MM-DD' (or an ISO datetime) and zero-dates
 * come through as '0000-00-00' or null. Parse the calendar date as LOCAL so the
 * picker shows the same day the database holds, whatever the browser timezone.
 */
export function toDateInput(value: unknown): Date | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const [, y, mo, d] = m;
  if (y === '0000' || mo === '00' || d === '00') return null;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function fromDateInput(date: Date | null | undefined): string {
  if (!date) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const YES_NO_OPTIONS = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
];

export const STATUS_OPTIONS = [
  { value: 'IN SERVICE', label: 'In Service' },
  { value: 'OUT OF SERVICE', label: 'Out of Service' },
  { value: 'TERMINATED', label: 'Terminated' },
];

export const PREFERENCE_OPTIONS = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'OTR', label: 'OTR' },
];

export const TURNOVER_OPTIONS = [
  { value: 'PREVENTABLE', label: 'Preventable' },
  { value: 'NON-PREVENTABLE', label: 'Non-preventable' },
];

/** `{recno,label}[]` lookups from the unit detail → SingleSelect options keyed by recno string. */
export const lookupOptions = (rows: { recno: number; label: string }[] | undefined) =>
  (rows ?? []).map((r) => ({ value: String(r.recno), label: r.label }));

export const lookupLabel = (rows: { recno: number; label: string }[] | undefined, recno: unknown): string =>
  rows?.find((r) => String(r.recno) === norm(recno))?.label ?? '';

// ---------------------------------------------------------------------------
// Phase 3: Cert / Payroll / Retention tabs + Truck grids
// ---------------------------------------------------------------------------

/** Two-letter US state / DC codes for the driver's license state select (no `states` lookup on the unit side). */
export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
] as const;
export const STATE_OPTIONS = US_STATES.map((s) => ({ value: s, label: s }));

export const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', '2X', '3X', '4X', '5X', '6X'] as const;
export const TSHIRT_OPTIONS = TSHIRT_SIZES.map((s) => ({ value: s, label: s }));
export const ACCOUNT_TYPE_OPTIONS = [{ value: 'CHECKING', label: 'Checking' }, { value: 'SAVINGS', label: 'Savings' }];

/** Driver scalar columns of the Cert tab (unit_children.py CERT_FIELDS). */
export const CERT_FIELDS = [
  'driverlicexpdate', 'driverlicnum', 'driverlicstate', 'hazmatexpdate', 'medcarddate', 'medcardexpdate', 'twicexpdate',
  'annualreviewexpdate', 'clearinghousedate', 'clearinghouseexpdate', 'endorsementtexpdate', 'endorsementpexpdate',
  'endorsementnexpdate', 'endorsementhexpdate', 'endorsementxexpdate', 'isotankapproved', 'reeferapproved',
] as const;

/** Driver scalar columns of the Retention tab (unit_children.py RETENTION_FIELDS). */
export const RETENTION_FIELDS = ['companyrep', 'dispatcher', 'driverbio', 'drivercomments', 'countryoforigin', 'tshirtsize'] as const;

/** V1 endorsement codes (driver.endorsement<code>expdate). */
export const ENDORSEMENTS: { code: string; field: string; label: string }[] = [
  { code: 'T', field: 'endorsementtexpdate', label: 'Double/Triple' },
  { code: 'P', field: 'endorsementpexpdate', label: 'Passenger' },
  { code: 'N', field: 'endorsementnexpdate', label: 'Tank' },
  { code: 'H', field: 'endorsementhexpdate', label: 'Hazmat' },
  { code: 'X', field: 'endorsementxexpdate', label: 'Tank+Hazmat' },
];

/** Editable unit columns of the Payroll tab (payroll.py PAYROLL_FIELDS); bank numbers are posted separately. */
export const PAYROLL_FIELDS = [
  'payrollhourly', 'payrolldisclosure', 'payrollgroup',
  'dd_bankname1', 'dd_accounttype1', 'dd_nameonaccount1', 'dd_amount1', 'dd_hold1',
  'dd_bankname2', 'dd_accounttype2', 'dd_nameonaccount2', 'dd_amount2', 'dd_hold2',
] as const;
export const BANK_FIELDS = ['dd_routing1', 'dd_account1', 'dd_routing2', 'dd_account2'] as const;

/** instype -> the unit flag column shown beside it on the Payroll tab (saved through the Driver tab's `unit` body). */
export const INS_FLAG_FIELDS: Record<string, string> = {
  'BOBTAIL': 'insbobtailgwi',
  'COMP & COLLISION': 'inscompcollisiongwi',
  'OCCUPATIONAL': 'insoccupationalgwi',
  'LIABILITY INSURANCE': 'insliabilitygwi',
};

/** Postable columns per child list (unit_children.py *_FIELDS). */
export const CHILD_FIELDS: Record<UnitChildKey, readonly string[]> = {
  restrictions: ['restriction', 'cardtype'],
  permits: ['permittype', 'permitdate', 'permitexpdate', 'permitnum'],
  owpermits: ['owpermittype', 'owpermitdate', 'owpermitexpdate', 'owpermitnum'],
  training: ['trainingtype', 'trainingsubjectrecno', 'trainingstartdate', 'trainingenddate'],
  eld: ['eldtype', 'eldreferencenum', 'eldstartdate', 'eldenddate'],
  insurance: ['instype', 'insamt', 'insstartdate', 'insenddate', 'insinceptiondate', 'insexpdate'],
  deductions: ['deductiontype', 'deductionrefno', 'deductionamt', 'deductionstartdate', 'deductionenddate'],
  surveys: ['surveytyperecno', 'surveydate', 'surveyrating', 'surveyedby', 'surveycomments'],
  awards: ['awardtype', 'awarddate'],
};
export const CHILD_KEYS = Object.keys(CHILD_FIELDS) as UnitChildKey[];

/**
 * Which drawer save step owns each child list. `surveys` is display-only on the Retention
 * tab (edited through the Safety Tables survey drawer, Phase 5), so no save step posts it.
 */
export const CHILD_KEYS_BY_TAB: Record<'truck' | 'cert' | 'payroll' | 'retention', UnitChildKey[]> = {
  truck: ['owpermits', 'eld'],
  cert: ['restrictions', 'permits', 'training'],
  payroll: ['insurance', 'deductions'],
  retention: ['awards'],
};

/** Only the listed keys of a record, missing ones as ''. */
export function pickFields(record: Record<string, any> | null | undefined, fields: readonly string[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of fields) out[f] = record?.[f] === null || record?.[f] === undefined ? '' : record[f];
  return out;
}

/** A DB date ('YYYY-MM-DD', ISO datetime, zero date, null) as a native date-input value. */
export const toIsoDate = (value: unknown): string => {
  const d = toDateInput(value);
  return d ? fromDateInput(d) : '';
};

export const isZeroDate = (value: unknown): boolean => toIsoDate(value) === '';

/**
 * The rows to post for one child list AND the local `_key` of each posted row, index-aligned,
 * so a 422 `{key, index, field}` can be mapped back to the row it came from.
 */
export function childSavePlan(original: Record<string, any>[], current: ChildRow[], fields: readonly string[]): { rows: Record<string, any>[]; keys: string[] } {
  const rows: Record<string, any>[] = [];
  const keys: string[] = [];
  for (const row of current) {
    const posted = childRowsForSave(original, [row], fields);
    if (posted.length) { rows.push(posted[0]); keys.push(String(row._key ?? row.recno ?? '')); }
  }
  return { rows, keys };
}

/** Per-row (`rowKey -> field -> message`) and scalar (`field -> message`) errors from a structured 422. */
export interface UnitSaveErrors {
  rows: Partial<Record<UnitChildKey, Record<string, Record<string, string>>>>;
  fields: Record<string, Record<string, string>>;
}
export const emptyUnitSaveErrors = (): UnitSaveErrors => ({ rows: {}, fields: {} });

/** `e.response.data.detail.errors` -> UnitSaveErrors; `plans` maps each posted child list to its row keys. */
export function mapUnitSaveErrors(detail: unknown, plans: Partial<Record<UnitChildKey, string[]>>): UnitSaveErrors | null {
  const errors = (detail as { errors?: UnitFieldError[] } | null)?.errors;
  if (!Array.isArray(errors) || !errors.length) return null;
  const out = emptyUnitSaveErrors();
  for (const err of errors) {
    const key = String(err.key ?? '');
    const field = String(err.field ?? '');
    const keys = plans[key as UnitChildKey];
    if (keys && err.index !== null && err.index !== undefined && keys[err.index] !== undefined) {
      const rowKey = keys[err.index];
      const byRow = (out.rows[key as UnitChildKey] ??= {});
      (byRow[rowKey] ??= {})[field] = err.message;
    } else {
      (out.fields[key] ??= {})[field] = err.message;
    }
  }
  return out;
}

/** Survey rating colour: >= 8 green, >= 7 amber, else red (blank -> muted). */
export function ratingClass(rating: unknown): string {
  const n = Number(rating);
  if (!Number.isFinite(n) || norm(rating) === '') return 'text-muted';
  if (n >= 8) return 'text-green-600 dark:text-green-400';
  if (n >= 7) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

/** `bankname1` style direct-deposit keys -> the `dd_*` unit columns the PUT expects. */
export function payrollFormFrom(p: { unit: Record<string, any>; direct_deposit: Record<string, any> } | null | undefined): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of PAYROLL_FIELDS) {
    const src = f.startsWith('dd_') ? p?.direct_deposit?.[f.slice(3)] : p?.unit?.[f];
    out[f] = src === null || src === undefined ? '' : src;
  }
  return out;
}

export const fmtMoney = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(v);
};

// ---- child-grid helpers (kept out of UnitChildGrid.tsx so that file exports only components) ----

/** Child rows not marked deleted. */
export const liveRows = (rows: ChildRow[]): ChildRow[] => rows.filter((r) => norm(r.deleted).toUpperCase() !== 'Y');

/** The local identity of a child row (`_key` from withKeys / newChildRow, else its recno). */
export const rowKeyOf = (row: ChildRow): string => String(row._key ?? row.recno ?? '');

/** `{recno,label}[]` / string[] lookups -> grid select options. */
export const gridOptions = (rows: ({ recno: number | string; label: string } | string)[] | undefined): { value: string; label: string }[] =>
  (rows ?? []).map((r) => (typeof r === 'string' ? { value: r, label: r } : { value: String(r.recno), label: r.label }));

/** A row is "current" when it has started and has not ended (ELD device in use, policy in force). */
export function isCurrentRow(start: unknown, end: unknown, today = fromDateInput(new Date())): boolean {
  const s = toIsoDate(start);
  const e = toIsoDate(end);
  if (!s || s > today) return false;
  return !e || e >= today;
}
